import { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Container, Row, Col, Card, Button } from 'react-bootstrap';
import { Plus, FileText, Edit, Trash2, Download, Calendar, Crown } from 'lucide-react';
import { supabase } from '../utils/supabaseClient';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';

const Dashboard = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [resumes, setResumes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isPremium, setIsPremium] = useState(false);

  useEffect(() => {
    fetchResumes();
  }, [user]);

  const fetchResumes = async () => {
    if (!user) return;
    try {
      const { data, error } = await supabase
        .from('resumes')
        .select('*')
        .eq('user_id', user.id)
        .order('updated_at', { ascending: false });

      if (error) throw error;
      setResumes(data || []);

      const { data: profile } = await supabase
        .from('profiles')
        .select('is_premium')
        .eq('id', user.id)
        .single();

      setIsPremium(profile?.is_premium || false);
    } catch (err) {
      console.error('Error fetching resumes:', err);
      toast.error('Failed to load resumes');
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this resume?')) return;

    try {
      const { error } = await supabase.from('resumes').delete().eq('id', id);
      if (error) throw error;
      setResumes(resumes.filter((r) => r.id !== id));
      toast.success('Resume deleted');
    } catch (err) {
      console.error(err);
      toast.error('Failed to delete resume');
    }
  };

  if (loading) {
    return (
      <div className="d-flex justify-content-center align-items-center" style={{ minHeight: '60vh', paddingTop: '80px' }}>
        <div className="text-muted">Loading your resumes...</div>
      </div>
    );
  }

  return (
    <div className="py-4" style={{ paddingTop: '80px' }}>
      <Container>
        {/* Header */}
        <div className="d-flex flex-wrap justify-content-between align-items-center mb-4">
          <div>
            <h1 className="h3 fw-bold text-dark mb-1">My Resumes</h1>
            <p className="text-muted mb-0">Manage and create professional resumes</p>
          </div>
          <Link
            to="/builder"
            className="btn btn-primary d-flex align-items-center gap-2"
          >
            <Plus size={20} />
            Create Resume
          </Link>
        </div>

        {/* Upgrade Banner */}
        <div
          className="rounded-3 p-4 mb-4 d-flex flex-wrap justify-content-between align-items-center"
          style={{ background: 'linear-gradient(to right, #eff6ff, #f5f3ff)' }}
        >
          <div className="d-flex align-items-center gap-3">
            <Crown
              size={24}
              className={isPremium ? 'text-warning' : 'text-secondary'}
            />
            <div>
              <p className="fw-medium text-dark mb-0">
                {isPremium ? 'Premium Member' : 'Free Plan'}
              </p>
              <p className="text-muted small mb-0">
                {isPremium
                  ? 'Enjoy unlimited resumes and premium templates'
                  : 'Upgrade to unlock premium templates and features'}
              </p>
            </div>
          </div>
          {!isPremium && (
            <Link to="/pricing" className="btn btn-primary btn-sm">
              Upgrade Monthly
            </Link>
          )}
        </div>

        {/* Resume List */}
        {resumes.length === 0 ? (
          <div className="text-center py-5 bg-white rounded-4 shadow-sm border">
            <FileText size={64} className="text-secondary mb-3" />
            <h3 className="h5 fw-semibold text-dark">No resumes yet</h3>
            <p className="text-muted mt-2">Create your first professional resume now</p>
            <Link to="/builder" className="btn btn-primary mt-3">
              Create Resume
            </Link>
          </div>
        ) : (
          <Row className="g-4">
            {resumes.map((resume) => (
              <Col md={6} lg={4} key={resume.id}>
                <Card className="h-100 shadow-sm border rounded-4">
                  <Card.Body className="p-4">
                    <div className="d-flex justify-content-between align-items-start">
                      <div className="flex-grow-1 overflow-hidden">
                        <h3 className="h6 fw-semibold text-dark text-truncate mb-0">
                          {resume.title || 'Untitled Resume'}
                        </h3>
                        <p className="text-muted small text-capitalize mb-0">
                          {resume.template_id || 'Professional'}
                        </p>
                      </div>
                      {resume.is_premium && (
                        <span className="badge bg-warning text-dark ms-2">Premium</span>
                      )}
                    </div>

                    <div className="d-flex align-items-center text-muted small mt-3">
                      <Calendar size={16} className="me-2" />
                      Updated: {new Date(resume.updated_at).toLocaleDateString()}
                    </div>

                    <div className="d-flex flex-wrap gap-2 mt-3">
                      <Link
                        to={`/builder/${resume.id}`}
                        className="btn btn-outline-secondary btn-sm flex-grow-1 d-flex align-items-center justify-content-center gap-1"
                      >
                        <Edit size={16} /> Edit
                      </Link>
                      <button
                        onClick={() => handleDelete(resume.id)}
                        className="btn btn-outline-danger btn-sm d-flex align-items-center gap-1"
                      >
                        <Trash2 size={16} /> Delete
                      </button>
                      <button
                        className="btn btn-primary btn-sm flex-grow-1 d-flex align-items-center justify-content-center gap-1"
                        disabled={!resume.is_complete}
                      >
                        <Download size={16} /> Download
                      </button>
                    </div>
                  </Card.Body>
                </Card>
              </Col>
            ))}
          </Row>
        )}
      </Container>
    </div>
  );
};

export default Dashboard;